import { useState, useCallback, useRef } from "react";
import { CUP_SIZES, AGE_GROUPS, DEFAULT_FILTER, type FilterState } from "@/lib/types.ts";

interface FilterPanelProps {
  filter: FilterState;
  onFilterChange: (filter: FilterState) => void;
  onClose?: () => void;
}

function RangeSlider({
  label,
  min,
  max,
  step = 1,
  value,
  onChange,
}: {
  label: string;
  min: number;
  max: number;
  step?: number;
  value: [number, number];
  onChange: (v: [number, number]) => void;
}) {
  const trackRef = useRef<HTMLDivElement>(null);
  const draggingRef = useRef<"low" | "high" | null>(null);

  const pctLow = ((value[0] - min) / (max - min)) * 100;
  const pctHigh = ((value[1] - min) / (max - min)) * 100;

  const clamp = (v: number) => Math.round(Math.max(min, Math.min(max, v)) / step) * step;

  const getValueFromX = (clientX: number) => {
    const track = trackRef.current;
    if (!track) return min;
    const rect = track.getBoundingClientRect();
    const pct = (clientX - rect.left) / rect.width;
    return clamp(min + pct * (max - min));
  };

  const handlePointerDown = (thumb: "low" | "high") => (e: React.PointerEvent) => {
    e.preventDefault();
    draggingRef.current = thumb;
    const target = e.currentTarget as HTMLElement;
    target.setPointerCapture(e.pointerId);
  };

  const handlePointerMove = (e: React.PointerEvent) => {
    if (!draggingRef.current) return;
    const v = getValueFromX(e.clientX);
    if (draggingRef.current === "low") {
      onChange([Math.min(v, value[1]), value[1]]);
    } else {
      onChange([value[0], Math.max(v, value[0])]);
    }
  };

  const handlePointerUp = () => {
    draggingRef.current = null;
  };

  const handleTrackClick = (e: React.MouseEvent) => {
    if (draggingRef.current) return;
    const v = getValueFromX(e.clientX);
    const distToLow = Math.abs(v - value[0]);
    const distToHigh = Math.abs(v - value[1]);
    if (distToLow <= distToHigh) {
      onChange([Math.min(v, value[1]), value[1]]);
    } else {
      onChange([value[0], Math.max(v, value[0])]);
    }
  };

  const handleInputChange = (thumb: "low" | "high", raw: string) => {
    const v = Number(raw);
    if (isNaN(v)) return;
    const clamped = clamp(v);
    if (thumb === "low") {
      onChange([Math.min(clamped, value[1]), value[1]]);
    } else {
      onChange([value[0], Math.max(clamped, value[0])]);
    }
  };

  return (
    <div className="flex flex-col gap-3 w-full">
      <span className="font-[var(--font-secondary)] text-sm font-semibold text-foreground">
        {label}
      </span>

      {/* Track */}
      <div
        ref={trackRef}
        className="relative h-1.5 w-full bg-secondary rounded cursor-pointer select-none"
        onClick={handleTrackClick}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
      >
        {/* Active range highlight */}
        <div
          className="absolute h-full bg-primary rounded"
          style={{ left: `${pctLow}%`, width: `${pctHigh - pctLow}%` }}
        />

        {/* Low thumb */}
        <div
          className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-5 h-5 rounded-full bg-primary border-2 border-card shadow-md cursor-grab active:cursor-grabbing hover:scale-110 transition-transform z-10"
          style={{ left: `${pctLow}%` }}
          onPointerDown={handlePointerDown("low")}
        />

        {/* High thumb */}
        <div
          className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-5 h-5 rounded-full bg-primary border-2 border-card shadow-md cursor-grab active:cursor-grabbing hover:scale-110 transition-transform z-10"
          style={{ left: `${pctHigh}%` }}
          onPointerDown={handlePointerDown("high")}
        />
      </div>

      {/* Number inputs for min/max */}
      <div className="flex items-center justify-between gap-2">
        <input
          type="number"
          min={min}
          max={value[1]}
          step={step}
          value={value[0]}
          onChange={(e) => handleInputChange("low", e.target.value)}
          className="w-16 h-7 px-2 text-center rounded-md border border-border bg-card text-foreground font-[var(--font-secondary)] text-xs outline-none focus:border-primary"
        />
        <span className="text-muted-foreground text-xs">〜</span>
        <input
          type="number"
          min={value[0]}
          max={max}
          step={step}
          value={value[1]}
          onChange={(e) => handleInputChange("high", e.target.value)}
          className="w-16 h-7 px-2 text-center rounded-md border border-border bg-card text-foreground font-[var(--font-secondary)] text-xs outline-none focus:border-primary"
        />
      </div>
    </div>
  );
}

export function FilterPanel({ filter, onFilterChange, onClose }: FilterPanelProps) {
  const [localFilter, setLocalFilter] = useState(filter);

  const update = useCallback((partial: Partial<FilterState>) => {
    setLocalFilter((prev) => ({ ...prev, ...partial }));
  }, []);

  const toggleCup = (cup: string) => {
    const next = localFilter.cupSizes.includes(cup)
      ? localFilter.cupSizes.filter((c) => c !== cup)
      : [...localFilter.cupSizes, cup];
    update({ cupSizes: next });
  };

  const toggleAge = (value: string) => {
    const next = localFilter.ageGroups.includes(value)
      ? localFilter.ageGroups.filter((a) => a !== value)
      : [...localFilter.ageGroups, value];
    update({ ageGroups: next });
  };

  const resetFilter = () => setLocalFilter(DEFAULT_FILTER);

  const apply = () => {
    onFilterChange(localFilter);
    onClose?.();
  };

  return (
    <div className="w-80 bg-card border-r border-border p-6 flex flex-col gap-7 h-full overflow-y-auto">
      <div className="flex items-center justify-between w-full">
        <h2 className="font-[var(--font-secondary)] text-lg font-semibold text-foreground">
          フィルター
        </h2>
        <button
          onClick={resetFilter}
          className="font-[var(--font-secondary)] text-[13px] text-primary bg-transparent border-0 cursor-pointer hover:underline"
        >
          リセット
        </button>
      </div>

      <RangeSlider
        label="バスト (cm)"
        min={70}
        max={120}
        value={localFilter.bustRange}
        onChange={(v) => update({ bustRange: v })}
      />

      <RangeSlider
        label="ウエスト (cm)"
        min={50}
        max={80}
        value={localFilter.waistRange}
        onChange={(v) => update({ waistRange: v })}
      />

      <RangeSlider
        label="ヒップ (cm)"
        min={70}
        max={110}
        value={localFilter.hipRange}
        onChange={(v) => update({ hipRange: v })}
      />

      <div className="flex flex-col gap-3 w-full">
        <span className="font-[var(--font-secondary)] text-sm font-semibold text-foreground">
          カップサイズ
        </span>
        <div className="flex flex-wrap gap-2">
          {CUP_SIZES.map((cup) => (
            <button
              key={cup}
              onClick={() => toggleCup(cup)}
              className={`w-9 h-9 rounded-lg text-xs font-medium cursor-pointer transition-colors border ${
                localFilter.cupSizes.includes(cup)
                  ? "bg-primary text-primary-foreground border-primary"
                  : "bg-secondary text-secondary-foreground border-transparent hover:bg-muted"
              }`}
            >
              {cup}
            </button>
          ))}
        </div>
      </div>

      <div className="flex flex-col gap-3 w-full">
        <span className="font-[var(--font-secondary)] text-sm font-semibold text-foreground">
          年代
        </span>
        <div className="flex flex-wrap gap-2">
          {AGE_GROUPS.map((group) => (
            <button
              key={group.value}
              onClick={() => toggleAge(group.value)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium cursor-pointer transition-colors border ${
                localFilter.ageGroups.includes(group.value)
                  ? "bg-primary text-primary-foreground border-primary"
                  : "bg-secondary text-secondary-foreground border-transparent hover:bg-muted"
              }`}
            >
              {group.label}
            </button>
          ))}
        </div>
      </div>

      <RangeSlider
        label="身長 (cm)"
        min={140}
        max={180}
        value={localFilter.heightRange}
        onChange={(v) => update({ heightRange: v })}
      />

      <button
        onClick={apply}
        className="w-full h-10 rounded-full bg-primary text-primary-foreground font-[var(--font-primary)] text-sm font-medium cursor-pointer border-0 hover:opacity-90 transition-opacity"
      >
        フィルターを適用
      </button>
    </div>
  );
}
